/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';
import { PropsStore } from '../../../../store/propsStore/propsStore';
import {
  TPropsConfiguration,
  TUpdateFieldConfiguration,
} from '../../../../store/propsStore/types';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { TModify, TRequireOnlyOne } from '../../../../types';
import { getSpecificParent } from '../../../../utils/dom';
import { addBoundary } from '../../../../utils/number';
import EventEmitter from '../../../../utils/util/eventEmitter';
import {
  TDataNode,
  TDataNodeContainer,
  TId,
  TTreeDataControllerConfig,
  TTreeDataControllerState,
} from './types';
import {
  getFirstNonFilteredChild,
  getLastVisibleChild,
  getNextChild,
  getNextNodeWithKey,
  getPreviousChild,
  selectAllNodesBetweenTwoNodes,
} from './util';

const trees: Record<string, TreeDataController> = {};
const treesRecordEmitter = new (class TreesRecordEmitter extends EventEmitter<{
  addController: { name: string; controller: TreeDataController };
}> {
  emit<K extends 'addController'>(
    eventName: K,
    params: {
      addController: {
        name: string;
        controller: TreeDataController;
      };
    }[K],
  ): void {
    super.emit(eventName, params);

    trees[params.name] = params.controller;
  }
})();

export function useTreeDataController<
  TreeType extends TreeDataController<any, any> = TreeDataController<any, any>,
>(name: string) {
  const [controller, setDataController] = useState<TreeDataController>(
    trees[name],
  );

  useEffect(() => {
    if (trees[name] !== controller) setDataController(trees[name]);

    return treesRecordEmitter.on('addController', (ev) => {
      if (ev.name === name) setDataController(ev.controller);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return controller as unknown as TreeType;
}

export function getTreeDataController<TreeType = TreeDataController>(
  name: string,
) {
  return trees[name] as unknown as TreeType;
}

export function deleteTreeDataController(name: string) {
  delete trees[name];
}

class TreeDataController<
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
> extends EventEmitter<{
  onArrowUpOnFirstElement: boolean;
  expandNode: NodeType;
  mustFocus: TId;
}> {
  #configuration: TTreeDataControllerConfig<NodeProps, NodeType>;

  get configuration() {
    return { ...this.#configuration };
  }

  setConfiguration(
    newConf: Partial<TTreeDataControllerConfig<NodeProps, NodeType>>,
  ) {
    this.#configuration = { ...this.#configuration, ...newConf };
  }

  hasApendedFirstChild = false;

  /**
   * Este array se usa para conocer los padres faltantes al momento de la
   * construcción del árbol, de forma de optimizar el proceso
   */
  missingParents: TId[] = [];

  nonEmittedUpdates: TId[] = [];

  get state() {
    return this.propsStore.getFieldProps<TTreeDataControllerState>(
      this.stateKey,
    );
  }

  stateKey = 'treeState';

  get stateStore() {
    return this.propsStore as unknown as PropsStore<TTreeDataControllerState>;
  }

  constructor(
    public name: string,
    configuration?: Partial<TTreeDataControllerConfig<NodeProps, NodeType>>,
    public propsStore = new PropsStore<NodeType>({
      logCommands: {
        propsStore: `treeProps_${name}`,
        propsLog: `treeLog_${name}`,
        propsSuscriptors: `propsSuscriptors_${name}`,
        updateProp: `updateProp_${name}`,
      },
    }),
  ) {
    super();

    this.#configuration = {
      emitUpdates: true,
      ...configuration,
    };

    this.setState(this.getInitialState());
    treesRecordEmitter.emit('addController', {
      name,
      controller: this as unknown as TreeDataController,
    });
    this.initRoot();
  }

  destructor() {
    this.propsStore.destructor();
  }

  append(
    node: Omit<
      TModify<
        NodeType,
        { children?: NodeType['children']; parentId?: NodeType['parentId'] }
      >,
      'nodeProps'
    > &
      Partial<Pick<NodeType, 'nodeProps'>>,
  ) {
    const newNode = { ...(node as NodeType), nodeProps: node.nodeProps ?? {} };
    // Evitar que se ingresen nodos duplicados
    if (this.propsStore.getFieldProps(newNode.id)?.children) {
      console.error('DUPLICATED NODE: The conflict origin is ', newNode, this);
      return;
    }

    // Se busca el padre, si el padre no existe se agrega a la raíz
    let father: TId;
    if (
      newNode.parentId !== undefined &&
      this.propsStore.getFieldProps(newNode.parentId)?.children
    ) {
      father = newNode.parentId;
    } else {
      father = 'root';
      if (newNode.parentId) this.missingParents.push(newNode.parentId);
      newNode.actualParentId = newNode.parentId;
      newNode.parentId = 'root';
    }

    this.propsStore.updateField(
      newNode.id,
      {
        ...newNode,
        isExpanded: this.#configuration.disableExpand || newNode.isExpanded,
        children: newNode.children ?? [],
      },
      { isUrgent: true },
    );
    this.setState({ length: this.state.length + 1 });

    // Se buscan elementos que estén en la raíz y sean hijos del nodo que se
    // está agregando. De esta forma al crear el árbol nos aseguramos que ningún
    // nodo quede afuera y evitamos iteraciones innecesarias
    if (this.missingParents.includes(newNode.id)) {
      this.propsStore.getFieldProps('root').children.forEach((currentChild) => {
        if (
          this.propsStore.getFieldProps(currentChild).actualParentId ===
          newNode.id
        ) {
          this.move(currentChild, newNode.id);
        }
      });
      this.missingParents = this.missingParents.filter(
        (current) => current !== newNode.id,
      );
    }

    // Se agrega el nodo a los hijos del padre
    this.propsStore.updateField(
      father,
      {
        children: [
          ...this.propsStore.getFieldProps(father).children,
          newNode.id,
        ],
      },
      { noEmit: !this.#configuration.emitUpdates },
    );

    if (!this.#configuration.emitUpdates) this.nonEmittedUpdates.push(father);

    if (!this.hasApendedFirstChild) {
      this.hasApendedFirstChild = true;
      this.setFocusedNode(newNode.id);
    }

    if (newNode.isExpanded) this.toggleNodeExpandedState(newNode.id, true);
    if (newNode.isSelected)
      this.setState({
        selectedNodes: [...this.state.selectedNodes, newNode.id],
      });
  }

  /**
   * Cuando se quieren agregar muchos nodos, es conveniente llamar primero al
   * método batchInit y luego de finalizar, al método batchFinish()
   */
  batchInit() {
    this.config({ emitUpdates: false });
    this.setState({ isLoading: true }, { isUrgent: true });
  }

  /**
   * Cuando se quieren agregar muchos nodos, es conveniente llamar primero al
   * método batchInit y luego de finalizar, al método batchFinish()
   */
  batchFinish() {
    this.config({ emitUpdates: true });
    setTimeout(() => this.setState({ isLoading: false }), 0);
  }

  config(newConf: Partial<TTreeDataControllerConfig<NodeProps, NodeType>>) {
    if (!this.#configuration.emitUpdates && newConf.emitUpdates) {
      this.nonEmittedUpdates.forEach((current) =>
        this.propsStore.updateField(current, {
          children: [...this.propsStore.getFieldProps(current).children],
        }),
      );
      this.nonEmittedUpdates = [];
    }
    this.#configuration = { ...this.#configuration, ...newConf };
  }

  deselectAll() {
    this.state.selectedNodes.forEach((currentId) =>
      this.propsStore.updateField(currentId, { isSelected: false }),
    );
    this.setState({ selectedNodes: [] });
  }

  forceEmitUpdate() {
    this.propsStore.updateField<{ forceUpdate: number }>('root', {
      forceUpdate:
        (this.propsStore.getFieldProps<{ forceUpdate: number }>('root')
          .forceUpdate ?? 0) + 1,
    });
  }

  getInitialState(): TTreeDataControllerState {
    return {
      expandedNodes: [],
      filteredNodes: [],
      focusedNode: null,
      isLoading: false,
      length: 0,
      selectedNodes: [],
    };
  }

  getNodesAsArray() {
    const allFields = { ...this.propsStore.fields };
    delete allFields[this.stateKey];
    delete allFields.root;
    return Object.values(allFields);
  }

  /**
   * Devuelve un array que contiene el id de todos los nodos pertenecientes al
   * árbol
   */
  getNodesIds() {
    const allFields = { ...this.propsStore.fields };
    delete allFields[this.stateKey];
    delete allFields.root;
    return Object.keys(allFields);
  }

  getParentId(parentId: TId) {
    return this.propsStore.getFieldProps(parentId).parentId;
  }

  /**
   * Este método permite controlar el comportamiento con el teclado desde fuera
   * del componente.
   */
  handleKey(ev: React.KeyboardEvent) {
    const focusedId = this.state.focusedNode as TId;
    const nodeProps = this.propsStore.getFieldProps(focusedId);

    const focusNode = (id: TId) => {
      if (
        this.#configuration.selectionMode === 'onFocus' &&
        !this.#configuration.isMultiple
      )
        this.setSelectedNodes([id]);
      this.setFocusedNode(id);
      this.emit('mustFocus', id);
    };

    if (ev.key === '*') {
      if (this.state.focusedNode === null) return;
      const parent = this.propsStore.getFieldProps(
        this.propsStore.getFieldProps(this.state.focusedNode).parentId,
      );
      parent.children.forEach((current) =>
        this.toggleNodeExpandedState(current, true),
      );
    } else if (ev.key.match(/^\w$/)) {
      const nextChildWithKey = getNextNodeWithKey(
        this,
        focusedId,
        ev.key,
        true,
      );
      if (nextChildWithKey !== null) {
        focusNode(nextChildWithKey);
      }
    } else
      switch (ev.code) {
        case 'Home': {
          const firstChild = getFirstNonFilteredChild(this, 'root');
          if (firstChild === null) return;
          if (ev.shiftKey && ev.ctrlKey && this.state.focusedNode !== null) {
            selectAllNodesBetweenTwoNodes(
              this,
              firstChild,
              this.state.focusedNode,
              true,
            );
          }
          focusNode(firstChild);
          break;
        }
        case 'End': {
          const lastVisibleChild = getLastVisibleChild(this, 'root', true);
          if (lastVisibleChild !== null) {
            if (ev.shiftKey && ev.ctrlKey && this.state.focusedNode !== null) {
              selectAllNodesBetweenTwoNodes(
                this,
                lastVisibleChild,
                this.state.focusedNode,
                true,
              );
            }
            focusNode(lastVisibleChild);
          }
          break;
        }
        case 'ArrowRight': {
          if (nodeProps.isLeaf) return;
          ev.preventDefault();
          if (nodeProps.isExpanded) {
            const firstChild = getFirstNonFilteredChild(this, nodeProps.id);
            if (firstChild !== null) {
              focusNode(firstChild);
            }
          } else this.toggleNodeExpandedState(nodeProps.id, true);
          break;
        }
        case 'ArrowLeft': {
          ev.preventDefault();
          if (nodeProps.isLeaf || !nodeProps.isExpanded) {
            if (
              nodeProps.parentId !== 'root' &&
              nodeProps.parentId !== undefined
            ) {
              focusNode(nodeProps.parentId);
            }
          } else this.toggleNodeExpandedState(nodeProps.id, false);
          break;
        }
        case 'ArrowUp': {
          ev.preventDefault();
          const prevChild = getPreviousChild(this, nodeProps.id, true);
          if (prevChild !== null) {
            if (ev.shiftKey) this.toggleNodeSelectedState(prevChild, true);
            focusNode(prevChild);
          } else this.emit('onArrowUpOnFirstElement', true);
          break;
        }
        case 'ArrowDown': {
          ev.preventDefault();
          const nextChild = getNextChild(this, nodeProps.id, false, true);
          if (nextChild !== null) {
            if (ev.shiftKey) this.toggleNodeSelectedState(nextChild, true);
            focusNode(nextChild);
          }
          break;
        }
        case 'Space': {
          if (this.#configuration.disableSelection) return;
          ev.preventDefault();
          this.toggleNodeSelectedState(nodeProps.id);
          break;
        }
        default:
          break;
      }
  }

  isNode(
    node: NodeType | TDataNodeContainer<NodeProps> | TId,
  ): node is NodeType {
    return typeof node === 'object' && 'parentId' in node;
  }

  isNodeContainer(
    node: NodeType | TDataNodeContainer<NodeProps> | TId,
  ): node is TDataNodeContainer<NodeProps> {
    return typeof node === 'object' && !this.isNode(node);
  }

  includes(searchNode: NodeType) {
    return !!this.propsStore.getFieldProps(searchNode.id);
  }

  initRoot() {
    this.propsStore.updateField(
      'root',
      { children: [], id: 'root' },
      { isUrgent: true },
    );
  }

  move(
    moveNode: TId,
    destinationNode: TId,
    afterOrBefore?: TRequireOnlyOne<{
      after: NodeType | TId;
      before: NodeType | TId;
      position: number;
    }>,
  ) {
    const currentFather = this.propsStore.getFieldProps(
      this.propsStore.getFieldProps(moveNode).parentId,
    );
    const newFather = this.propsStore.getFieldProps(destinationNode);

    if (!currentFather) {
      console.warn('The current node does not belong to the tree.', moveNode);
      return;
    }

    if (!newFather) {
      console.warn(
        'The destination node does not belong to the tree. No action will be made',
        destinationNode,
      );
      return;
    }

    currentFather.children = currentFather.children?.filter(
      (search) => search !== moveNode,
    );
    if (!newFather.children) newFather.children = [];
    if (!afterOrBefore) newFather.children.push(moveNode);
    else if (afterOrBefore.position) {
      newFather.children.splice(afterOrBefore.position - 1, 0, moveNode);
    } else {
      const key = afterOrBefore.after
        ? afterOrBefore.after
        : afterOrBefore.before;

      const relatedNodeKey = key && this.isNode(key) ? key.id : key;
      const relatedNodeIndex = newFather.children.findIndex(
        (search) => search === relatedNodeKey,
      );

      const actualIndex = addBoundary(
        afterOrBefore.before !== undefined
          ? relatedNodeIndex
          : relatedNodeIndex + 1,
        0,
      );

      if (actualIndex === -1 || actualIndex === newFather.children.length)
        newFather.children.push(moveNode);
      else newFather.children.splice(actualIndex, 0, moveNode);
    }

    this.propsStore.updateField(currentFather.id, {
      children: [...(currentFather.children ?? [])],
    });
    this.propsStore.updateField(newFather.id, {
      children: [...newFather.children],
    });
    this.propsStore.updateField(moveNode, {
      parentId: newFather.id,
    });
  }

  /**
   * Borra el nodo del árbol, y dependiendo del parámetro removeChildren, borra
   * también sus hijos.
   *
   * @param removeChildren - Si se pasa en false, los nodos hijos son movidos al root
   */
  remove(removeNode: NodeType | TId, removeChildren = true) {
    const removeNodeId = this.isNode(removeNode) ? removeNode.id : removeNode;

    const removingNode = this.propsStore.getFieldProps(removeNodeId);
    if (!removingNode) return;

    const father = this.propsStore.getFieldProps(removingNode.parentId);
    if (father) {
      this.propsStore.updateField(father.id, {
        children: father.children?.filter((search) => search !== removeNodeId),
      });
      if (this.propsStore.getFieldProps(father.id).children.length === 0) {
        if (this.configuration.hideEmptyFolders) this.remove(father.id);
      }
    }

    removingNode.children?.forEach((current) => {
      if (removeChildren) {
        this.remove(current);
      } else {
        this.move(current, 'root');
      }
    });

    this.setState({
      selectedNodes: this.state.selectedNodes.filter(
        (current) => current !== removeNodeId,
      ),
      focusedNode:
        this.state.focusedNode === removeNodeId ? null : this.state.focusedNode,
      length: this.state.length - 1,
    });
    this.propsStore.removeField(removeNodeId);
  }

  removeMultiple(nodes: TId[], removeChildren = true) {
    nodes.forEach((current) => this.remove(current, removeChildren));
  }

  /**
   * Borra todos los nodos del árbol.
   */
  removeAll() {
    this.hasApendedFirstChild = false;
    this.setState({ focusedNode: null }, { isUrgent: true });
    if (this.#configuration.emitUpdates) this.setSelectedNodes([]);
    this.initRoot();
    Object.keys(this.propsStore.fields).forEach((current) => {
      if (!['root', this.stateKey].includes(current))
        this.propsStore.removeField(current);
    });
  }

  selectAll() {
    this.setState({
      selectedNodes: this.getNodesIds(),
    });
    this.state.selectedNodes.forEach((currentId) =>
      this.propsStore.updateField(currentId, { isSelected: true }),
    );
  }

  setFocusedNode(key: TId) {
    if (
      this.state.focusedNode !== null &&
      this.propsStore.getFieldProps(this.state.focusedNode)
    )
      this.propsStore.updateField(this.state.focusedNode, { isFocused: false });
    this.setState({
      focusedNode: key,
    });
    this.propsStore.updateField(key, { isFocused: true });
  }

  setExpandedNodes(nodes: TId[], force = false) {
    if (this.#configuration.disableExpand && !force) return;
    this.state.expandedNodes.forEach((current) =>
      this.propsStore.updateField(current, { isExpanded: false }),
    );
    this.setState({
      expandedNodes: nodes.filter((current) => {
        const nodeProps = this.propsStore.getFieldProps(current);
        if (nodeProps?.isLeaf !== false || force)
          this.propsStore.updateField(current, { isExpanded: true });
        return nodeProps?.isLeaf !== false || force;
      }),
    });
  }

  setSelectedNodes(nodes: TId[], force = false) {
    if (this.#configuration.disableSelection && !force) return;
    this.state.selectedNodes.forEach((current) =>
      this.propsStore.updateField(current, { isSelected: false }),
    );
    this.setState({
      selectedNodes: nodes.filter((current) => {
        const nodeProps = this.propsStore.getFieldProps(current);
        if (nodeProps?.isSelectable !== false || force)
          this.propsStore.updateField(current, { isSelected: true });
        return nodeProps?.isSelectable !== false || force;
      }),
    });
  }

  setSelectedNodesByClickEvent(ev: React.MouseEvent) {
    if (this.#configuration.disableSelection) return;
    const nodeKey = getSpecificParent(
      ev.target as HTMLElement,
      (current) => current.getAttribute('role') === 'treeitem',
    )?.getAttribute('data-key');
    if (nodeKey) {
      const nodeProps = this.propsStore.getFieldProps(nodeKey);
      if (nodeProps.isDisabled || nodeProps.isSelectable === false) return;

      const previousSelectionKeys = [...this.state.selectedNodes];
      let newSelection: TId[];

      if (this.#configuration.isMultiple) {
        newSelection = nodeProps.isSelected
          ? previousSelectionKeys.filter((current) => current !== nodeKey)
          : [...previousSelectionKeys, nodeKey];
      } else {
        newSelection = nodeProps.isSelected ? [] : [nodeKey];
      }

      this.setState({
        selectedNodes: newSelection,
      });

      this.propsStore.updateField(nodeKey, {
        isSelected: !nodeProps.isSelected,
      });
      previousSelectionKeys.forEach((current) => {
        if (!newSelection.includes(current))
          this.propsStore.updateField(current, { isSelected: false });
      });
    } else console.warn('Cannot set selection, no treeitem found', ev);
  }

  setState(
    updateProps: Partial<TTreeDataControllerState>,
    conf?: TUpdateFieldConfiguration,
  ) {
    this.propsStore.updateField<TTreeDataControllerState>(
      this.stateKey,
      {
        ...this.state,
        ...updateProps,
      },
      conf,
    );
  }

  toggleNodeExpandedState(key: TId, shouldExpand?: boolean) {
    if (this.#configuration.disableExpand) return;

    const nodeProps = this.propsStore.getFieldProps(key);
    if (nodeProps.isDisabled || nodeProps.isLeaf || nodeProps.isLoading) return;

    if (this.#configuration.onLoadData && !nodeProps.hasLoaded) {
      this.propsStore.updateField(key, { isLoading: true });
      void this.#configuration.onLoadData(nodeProps).finally(() => {
        this.propsStore.updateField(key, {
          isLoading: false,
          isExpanded: true,
          hasLoaded: true,
        });

        this.setState({
          expandedNodes: [...this.state.expandedNodes, key],
        });
        this.#configuration.onExpand?.(this.propsStore.getFieldProps(key));
      });
    } else {
      const { expandedNodes } = this.state;
      const shouldExpandInner =
        shouldExpand !== undefined
          ? shouldExpand
          : !expandedNodes.includes(key);
      if (this.propsStore.getFieldProps(key)?.isDisabled) return;

      this.setState({
        expandedNodes: shouldExpandInner
          ? [...expandedNodes, key]
          : expandedNodes.filter((current) => current !== key),
      });
      this.propsStore.updateField(key, { isExpanded: shouldExpandInner });
      this.#configuration.onExpand?.(this.propsStore.getFieldProps(key));
    }
  }

  toggleNodeSelectedState(key: TId, isSelected?: boolean) {
    if (this.#configuration.disableSelection) return;
    const nodeProps = this.propsStore.getFieldProps(key);
    if (nodeProps.isDisabled || nodeProps.isSelectable === false) return;

    const shouldSelect =
      isSelected !== undefined
        ? isSelected
        : !this.state.selectedNodes.includes(key);

    if (
      shouldSelect &&
      !this.#configuration.isMultiple &&
      this.state.selectedNodes[0]
    )
      this.toggleNodeSelectedState(this.state.selectedNodes[0], false);
    this.propsStore.updateField(key, { isSelected: shouldSelect });
    this.setState({
      selectedNodes: shouldSelect
        ? [...this.state.selectedNodes, key]
        : this.state.selectedNodes.filter((current) => current !== key),
    });
  }
}

/**
 * Este hook permite escuchar las props de un árbol, para ello es necesario
 * pasar el handler.
 *
 * @param handler
 * @param configuration
 * @returns
 */
export function useTreeSelector<
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
  Selected = Record<string, unknown>,
  PropsType extends Record<string, unknown> = TTreeDataControllerState,
>(
  handler: TreeDataController<NodeProps, NodeType>,
  configuration?: TPropsConfiguration<Selected, PropsType>,
): Selected {
  return usePropsSelector<Selected, PropsType>(
    handler?.stateKey ?? '__NO__TREE__KEY__',
    {
      propsStore: handler?.propsStore as unknown as PropsStore<PropsType>,
      ...configuration,
    },
  );
}

/**
 * Este hook permite escuchar las props de un árbol, para ello es necesario
 * pasar el handler.
 *
 * @param handler
 * @param configuration
 * @returns
 */
export function useTreeSelectorByName<
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
  Selected = Record<string, unknown>,
  PropsType extends Record<string, unknown> = TTreeDataControllerState,
>(
  treeName: string,
  configuration?: TPropsConfiguration<Selected, PropsType>,
): {
  handler: TreeDataController<NodeProps, NodeType>;
  selection: Selected;
} {
  const handler =
    useTreeDataController<TreeDataController<NodeProps, NodeType>>(treeName);
  const selection = usePropsSelector<Selected, PropsType>(
    handler?.stateKey ?? '__NO__TREE__KEY__',
    {
      propsStore: handler?.propsStore as unknown as PropsStore<PropsType>,
      ...configuration,
    },
  );
  return { selection, handler };
}

export default TreeDataController;
